import { Injectable } from '@angular/core';
import {
  EXPIRATION_BUFFER_MS,
  REMEMBER_ME_KEY,
  STORAGE_KEY,
} from '@ieCore/consts/auth.consts';
import {
  AuthDto,
  AuthResponseDto,
  RefreshTokenDto,
} from 'shared-general-libs/dto/auth';
import { AuthRepository } from '@ieCore/repositories/auth.repository';
import {
  requestLoading,
  RequestLoadingType,
} from '@ieCore/helpers/request-loading.helper';
import { map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NavService } from '@ieCore/services/nav.service';
import { BehaviorSubject, filter, Subject, take, takeUntil } from 'rxjs';
import * as UserActions from '@ieApp/store/user/user.actions';
import { Store } from '@ngrx/store';
import { UserType } from 'shared-general-libs/type/user';
import { selectUser } from '@ieApp/store/user/user.selectors';
import logger from '@ieShared/utils/logger.utils';
import { TopicService } from '@ieCore/services/topic.service';
import { Permission } from 'shared-general-libs/enum/permission';
import { OrganizationService } from '@ieCore/services/organization.service';
import { safeReturnUrl } from '@ieCore/utils/return-url.utils';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private refreshTokenRequested = false;
  /** Fires on every `removeAuthData()`. Each login's post-load subscription
   * completes on it: `take(1)` alone cannot end a subscription that never
   * consumed an emission (a load still in flight when the session died), and
   * that armed leftover would consume the NEXT login's fresh user — the
   * cross-login returnUrl leak in its in-flight form (compose-spec-pinned;
   * removing this line re-reds exactly that pin). */
  private sessionInvalidated$ = new Subject<void>();
  public refreshTokenSubject = new BehaviorSubject<boolean | null>(null);
  public storage = sessionStorage;
  getEnumList = requestLoading<{ [p: string]: string }>();

  constructor(
    private authRepository: AuthRepository,
    private topicService: TopicService,
    private store: Store,
    private navService: NavService,
    private router: Router,
    private organizationService: OrganizationService,
  ) {
    const storage = localStorage.getItem(REMEMBER_ME_KEY);
    storage === 'true'
      ? (this.storage = localStorage)
      : (this.storage = sessionStorage);
  }

  isAuthenticated(): boolean {
    return !!this.storage.getItem(STORAGE_KEY);
  }

  storeAuthData(payload: AuthResponseDto, rememberMe: boolean) {
    localStorage.setItem(REMEMBER_ME_KEY, rememberMe.toString());

    if (rememberMe) {
      this.storage = localStorage;
    }

    this.storage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }

  getAuthData(): AuthResponseDto {
    return JSON.parse(this.storage.getItem(STORAGE_KEY) || '{}');
  }

  getUserIdFromToken(token?: string): string {
    const authToken = token || this.getAuthData().accessToken;
    const obj = JSON.parse(atob(authToken.split('.')[1]));
    return obj.sub;
  }

  getUserTypeFromToken(token?: string): UserType {
    const authToken = token || this.getAuthData().accessToken;
    const obj = JSON.parse(atob(authToken!.split('.')[1]));
    return obj.type;
  }

  removeAuthData() {
    this.storage.removeItem(STORAGE_KEY);
    localStorage.removeItem(REMEMBER_ME_KEY);
    // Every session invalidation funnels through here (the interceptor's 401
    // branch, the idle timeout, logout, the init-app load failure, the forced
    // password reset). The NgRx user must fall with the token: a populated
    // store would let the next login's `filter(user !== null)` navigate on
    // YESTERDAY'S user — old permissions, old orgUiConfig — and then navigate
    // AGAIN when the fresh /users/me landed (Wave-5 gate, finding 3).
    this.store.dispatch(UserActions.clear());
    this.sessionInvalidated$.next();
  }

  tokenIsExpired() {
    const authData = this.getAuthData();
    return new Date().getTime() > authData.expiresIn - EXPIRATION_BUFFER_MS;
  }

  setRefreshTokenRequested() {
    this.refreshTokenRequested = true;
  }

  setNewTokenReady() {
    this.refreshTokenSubject.next(true);
    this.refreshTokenRequested = false;
    this.refreshTokenSubject.next(false);
  }

  isNewTokenRequested() {
    return this.refreshTokenRequested;
  }

  /**
   * `requestedUrl` is the raw, attacker-reachable `returnUrl` captured by
   * `authGuard`. It is validated through `safeReturnUrl()` before it is used;
   * omitting it (every existing two-argument caller) keeps today's navigation
   * exactly as it is.
   */
  login(
    payload: AuthDto,
    rememberMe: boolean,
    requestedUrl?: string | null,
  ): RequestLoadingType<AuthResponseDto> {
    return requestLoading(
      this.authRepository.login(payload).pipe(
        tap((res) => {
          this.storeAuthData(res, rememberMe);
          this.store.dispatch(UserActions.load());

          this.store
            .select(selectUser)
            .pipe(
              filter((user) => user !== null),
              // One login = one navigation (delta re-gate finding 1): without
              // take(1) this subscription outlived its login, and a LATER
              // login's fresh emission replayed it — including a previous
              // user's captured returnUrl on a shared machine. takeUntil
              // closes the case take(1) cannot: a login whose load never
              // resolved (session invalidated mid-flight) leaves this armed
              // for the NEXT login's emission — same leak, in-flight form.
              take(1),
              takeUntil(this.sessionInvalidated$),
            )
            .subscribe((user) => {
              const userType = this.getUserTypeFromToken();

              this.getEnumList =
                this.organizationService.getEnumTranslationList();
              this.getEnumList.response.subscribe((d) => {
                this.organizationService.enumTranslationList = d;
              });

              if (user!.permissions.includes(Permission.Topic.VIEW)) {
                this.topicService.storeTopics().then();
              }

              const returnUrl = safeReturnUrl(requestedUrl, userType);

              // Whenever a returnUrl was in play — consumed OR rejected — the
              // current history entry is `/login?returnUrl=…`, which carries a copy
              // of the attempted URL and therefore of anything in its query string.
              // Replace it instead of pushing on top of it so it does not stay
              // recoverable from browser history. A plain two-argument login never
              // had such an entry and keeps today's push semantics exactly.
              const navigate = (commands: unknown[]) =>
                requestedUrl
                  ? this.router.navigate(commands, { replaceUrl: true })
                  : this.router.navigate(commands);

              if (returnUrl) {
                this.router
                  .navigateByUrl(returnUrl, { replaceUrl: true })
                  .then(() => logger.log('Navigated to the requested page'));

                return;
              }

              const homeRoute = this.navService.getRouteByUserTypeAndPermission(
                userType,
                user?.permissions,
              );

              if (homeRoute === undefined) {
                navigate(this.navService.to.notFound()).then(() =>
                  logger.log('No accessible route: navigated to not found'),
                );

                return;
              }

              navigate([homeRoute]).then(() =>
                logger.log(`Navigated to ${userType} dashboard`),
              );
            });
        }),
        map((res) => res),
      ),
    );
  }

  refreshToken(refreshToken: string): RequestLoadingType<AuthResponseDto> {
    const storage = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
    return requestLoading(
      this.authRepository.refresh({ refreshToken }).pipe(
        tap((res) => this.storeAuthData(res, storage)),
        map((res) => res),
      ),
    );
  }

  logout(payload: RefreshTokenDto): RequestLoadingType<void> {
    return requestLoading(
      this.authRepository.logout(payload).pipe(
        tap(() => {
          this.removeAuthData();

          this.store.dispatch(UserActions.clear());

          this.router
            .navigate(this.navService.to.login())
            .then(() => logger.log('Navigated to login'));
        }),
      ),
    );
  }
}
